/* Template Variables — admin API client.
   Consumed by Settings → Variables. These rows drive the variable picker
   in the Communication Template editor (via /email-templates/meta) and the
   sample values used for preview. The rendered token is {{key}}. */

import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export type VariableStatusFilter = "all" | "active" | "inactive";

export interface ITemplateVariable {
  id: number;
  name: string;
  key: string;
  token: string;
  category: string;
  sample_value: string | null;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  updated_at: string | null;
}

export interface ITemplateVariableInput {
  name: string;
  key?: string;
  category: string;
  sample_value?: string | null;
  description?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

/** Suggested categories for the picker grouping. Free text is allowed too. */
export const VARIABLE_CATEGORIES = [
  "Authentication",
  "Customer",
  "Lead",
  "Quote",
  "Invoice",
  "Company",
  "Sender",
  "System",
  "Links",
  "Misc",
];

interface ApiEnvelope<T> {
  code: number;
  status: boolean;
  message?: string;
  data: T;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await trackedFetch(`${API_URL}${path}`, { ...init, headers: headers() });
  const json: ApiEnvelope<T> = await res.json();
  if (!json.status) throw new Error(json.message || "Request failed");
  return json.data;
}

export const fetchTemplateVariables = (
  filters: { search?: string; category?: string; status?: VariableStatusFilter } = {},
): Promise<ITemplateVariable[]> => {
  const qs = new URLSearchParams();
  if (filters.search) qs.set("search", filters.search);
  if (filters.category) qs.set("category", filters.category);
  if (filters.status && filters.status !== "all") qs.set("status", filters.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return call<ITemplateVariable[]>(`/template-variables${suffix}`, { method: "GET" });
};

export const createTemplateVariable = (data: ITemplateVariableInput): Promise<ITemplateVariable> =>
  call<ITemplateVariable>(`/template-variables`, { method: "POST", body: JSON.stringify(data) });

export const updateTemplateVariable = (id: number, data: ITemplateVariableInput): Promise<ITemplateVariable> =>
  call<ITemplateVariable>(`/template-variables/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteTemplateVariable = (id: number): Promise<null> =>
  call<null>(`/template-variables/${id}`, { method: "DELETE" });

export const setTemplateVariableActive = (id: number, isActive: boolean): Promise<ITemplateVariable> =>
  call<ITemplateVariable>(`/template-variables/${id}/active`, {
    method: "POST",
    body: JSON.stringify({ is_active: isActive }),
  });

/** Client-side key normaliser — mirrors the backend (lowercase snake_case). */
export const normalizeVariableKey = (raw: string): string =>
  (raw || "")
    .trim()
    .replace(/^\{\{|\}\}$/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
